/**
 * BusinessLayout (B1.1) — Main authenticated shell for the Business app.
 *
 * Authority: UX Blueprint §4 (Navigation Architecture).
 *
 * Structure:
 *   ┌─────────────────────────────────────────────┐
 *   │  [TenantSwitcher] … [Notifications][User]   │ ← TopBar (56px)
 *   ├───────────┬─────────────────────────────────┤
 *   │           │                                 │
 *   │  Sidebar  │         Main (<Outlet/>)        │
 *   │  (240px)  │                                 │
 *   │           │                                 │
 *   └───────────┴─────────────────────────────────┘
 *
 * Tablet-first invariants:
 *   - Sidebar items: 56px min-height, icon + label
 *   - No hover states on touch devices (@media(hover:none))
 *   - Sidebar auto-collapses below 1024px (iPad portrait)
 *   - Main content area has safe-area padding for notch/home-indicator
 *
 * This is a Claude-owned smart wrapper. Lovable may provide visual polish
 * per brief B1.1 but the structural/navigational logic lives here.
 */
import {
  Bed,
  Bell,
  CalendarBlank,
  ChartLineUp,
  Gear,
  House,
  ListBullets,
  Package,
  PlugsConnected,
  Receipt,
  Scales,
  Scissors,
  Siren,
  ShieldStar,
  SignOut,
  Sparkle,
  UsersThree,
  Wallet,
} from '@phosphor-icons/react';
import { type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../shared/hooks/useAuth';
import { TenantSwitcher } from '../components/TenantSwitcher';
import { cn } from '../shared/utils/cn';

interface NavItem {
  to: string;
  labelKey: string;
  icon: ReactNode;
}

const MAIN_NAV: NavItem[] = [
  { to: '/dashboard', labelKey: 'nav.dashboard', icon: <House size={22} weight="regular" /> },
  { to: '/today', labelKey: 'nav.today', icon: <ListBullets size={22} weight="regular" /> },
  { to: '/calendar', labelKey: 'nav.calendar', icon: <CalendarBlank size={22} weight="regular" /> },
  { to: '/services', labelKey: 'nav.services', icon: <Package size={22} weight="regular" /> },
  { to: '/staff', labelKey: 'nav.staff', icon: <UsersThree size={22} weight="regular" /> },
  { to: '/grooming', labelKey: 'nav.grooming', icon: <Scissors size={22} weight="regular" /> },
  { to: '/boarding', labelKey: 'nav.boarding', icon: <Bed size={22} weight="regular" /> },
  { to: '/customers', labelKey: 'nav.customers', icon: <UsersThree size={22} weight="regular" /> },
  { to: '/inventory', labelKey: 'nav.inventory', icon: <Package size={22} weight="regular" /> },
  { to: '/cashier', labelKey: 'nav.cashier', icon: <Wallet size={22} weight="regular" /> },
  { to: '/invoices', labelKey: 'nav.invoices', icon: <Receipt size={22} weight="regular" /> },
  { to: '/reminders', labelKey: 'nav.reminders', icon: <Siren size={22} weight="regular" /> },
  { to: '/revenue/leak', labelKey: 'nav.revenueLeak', icon: <ChartLineUp size={22} weight="regular" /> },
  { to: '/reconciliation', labelKey: 'nav.reconciliation', icon: <Scales size={22} weight="regular" /> },
  { to: '/connect', labelKey: 'nav.connect', icon: <PlugsConnected size={22} weight="regular" /> },
  { to: '/reports', labelKey: 'nav.reports', icon: <Sparkle size={22} weight="regular" /> },
];

const SECONDARY_NAV: NavItem[] = [
  { to: '/settings', labelKey: 'nav.settings', icon: <Gear size={22} weight="regular" /> },
];

const ADMIN_ENTRY: NavItem = {
  to: '/admin/business-profiles',
  labelKey: 'adminNav.badge',
  icon: <ShieldStar size={22} weight="regular" />,
};

export function BusinessLayout(): JSX.Element {
  const { t } = useTranslation('layout');
  const navigate = useNavigate();
  const location = useLocation();
  const user = useAuth((s) => s.user);
  const logout = useAuth((s) => s.logout);

  const handleLogout = () => {
    logout();
    navigate('/login', { replace: true });
  };

  return (
    <div className="flex h-screen min-h-screen bg-biz-background text-biz-foreground">
      {/* SIDEBAR */}
      <aside
        className={cn(
          'w-[240px] shrink-0 border-r border-biz-border bg-biz-surface',
          'flex flex-col',
          'hidden lg:flex',
          'z-biz-sidebar',
        )}
      >
        <div className="px-biz-card-lg py-biz-content-lg border-b border-biz-border">
          <div className="flex items-center gap-biz-content-lg">
            <div
              aria-hidden="true"
              className="w-10 h-10 rounded-biz-card bg-biz-primary text-biz-primary-foreground flex items-center justify-center font-bold biz-heading"
            >
              B
            </div>
            <div className="flex flex-col">
              <span className="text-biz-base font-semibold biz-heading">Paptoq</span>
              <span className="text-biz-xs text-biz-muted-foreground uppercase tracking-wider">
                Business
              </span>
            </div>
          </div>
        </div>

        <nav
          aria-label={t('nav.dashboard')}
          className="flex-1 overflow-y-auto py-biz-content-lg"
        >
          <ul className="space-y-1 px-biz-content">
            {MAIN_NAV.map((item) => (
              <SidebarItem key={item.to} item={item} active={isActive(location.pathname, item.to)} />
            ))}
          </ul>
        </nav>

        <div className="border-t border-biz-border py-biz-content-lg">
          <ul className="space-y-1 px-biz-content">
            {SECONDARY_NAV.map((item) => (
              <SidebarItem key={item.to} item={item} active={isActive(location.pathname, item.to)} />
            ))}
            {user?.platformRole === 'platform_admin' ? (
              <SidebarItem
                item={ADMIN_ENTRY}
                active={isActive(location.pathname, ADMIN_ENTRY.to)}
              />
            ) : null}
          </ul>
        </div>
      </aside>

      {/* MAIN COLUMN */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* TOPBAR */}
        <header
          className={cn(
            'flex items-center gap-biz-content-lg',
            'px-biz-card-lg border-b border-biz-border',
            'min-h-biz-touch-primary',
            'bg-biz-surface-elevated',
            'z-biz-topbar',
            'biz-safe-area',
          )}
        >
          <TenantSwitcher />

          <div className="flex-1" />

          <button
            type="button"
            aria-label={t('topbar.notifications')}
            className={cn(
              'relative inline-flex items-center justify-center',
              'min-h-biz-touch min-w-biz-touch rounded-biz-btn',
              'text-biz-muted-foreground hover:bg-biz-muted hover:text-biz-foreground',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-biz-ring',
              'transition-colors duration-biz-fast',
            )}
          >
            <Bell weight="regular" size={22} aria-hidden="true" />
          </button>

          <button
            type="button"
            onClick={handleLogout}
            aria-label={t('topbar.userMenu')}
            className={cn(
              'flex items-center gap-biz-content',
              'min-h-biz-touch px-biz-content-lg rounded-biz-btn',
              'hover:bg-biz-muted',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-biz-ring',
              'transition-colors duration-biz-fast',
            )}
          >
            <div
              aria-hidden="true"
              className="w-8 h-8 rounded-biz-full bg-biz-primary-subtle text-biz-primary flex items-center justify-center text-biz-sm font-semibold"
            >
              {user ? user.displayName.slice(0, 1).toUpperCase() : '?'}
            </div>
            <span className="hidden md:inline text-biz-sm font-medium max-w-[120px] truncate">
              {user?.displayName ?? ''}
            </span>
            <SignOut weight="regular" size={18} aria-hidden="true" className="text-biz-muted-foreground" />
          </button>
        </header>

        {/* CONTENT */}
        <main
          id="main"
          className="flex-1 overflow-y-auto bg-biz-background biz-safe-area"
          tabIndex={-1}
        >
          <Outlet />
        </main>
      </div>
    </div>
  );
}

function SidebarItem({
  item,
  active,
}: {
  item: NavItem;
  active: boolean;
}): JSX.Element {
  const { t } = useTranslation('layout');
  return (
    <li>
      <NavLink
        to={item.to}
        aria-current={active ? 'page' : undefined}
        className={cn(
          'flex items-center gap-biz-content-lg',
          'min-h-biz-touch px-biz-content-lg rounded-biz-btn',
          'text-biz-sm font-medium',
          'transition-colors duration-biz-fast',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-biz-ring',
          active
            ? 'bg-biz-primary text-biz-primary-foreground'
            : 'text-biz-foreground hover:bg-biz-muted',
        )}
      >
        <span aria-hidden="true" className="shrink-0">
          {item.icon}
        </span>
        <span className="truncate">{t(item.labelKey)}</span>
      </NavLink>
    </li>
  );
}

function isActive(pathname: string, to: string): boolean {
  if (to === '/') return pathname === '/';
  return pathname === to || pathname.startsWith(`${to}/`);
}
